import type {
  ISecapTemplateCompilation,
  ISecapTemplateCompilationField,
  SecapJsonObject,
  SecapTemplateCompilationMode,
  SecapTemplateCompilationStatus,
} from '../../interfaces/secap/ISecapApi';
import type {
  ISecapTemplateCompilationFieldView,
  ISecapTemplateCompilationHistoryItem,
  ISecapTemplateCompilationViewModel,
  SecapTemplateTone,
} from '../../interfaces/secap/ISecapTemplateCompilation';

const COMPILATION_STATUS: Readonly<
  Record<SecapTemplateCompilationStatus, { readonly label: string; readonly tone: SecapTemplateTone }>
> = {
  preparing: { label: 'Preparazione', tone: 'neutral' },
  awaiting_confirmation: { label: 'Da revisionare', tone: 'warning' },
  ready: { label: 'Pronto', tone: 'positive' },
  completed: { label: 'Completato', tone: 'positive' },
  failed: { label: 'Errore', tone: 'negative' },
};

const FIELD_STATUS: Readonly<Record<string, { readonly label: string; readonly tone: SecapTemplateTone }>> = {
  ready: { label: 'Pronto', tone: 'positive' },
  pending_confirmation: { label: 'Conferma richiesta', tone: 'warning' },
  conflict: { label: 'Conflitto', tone: 'warning' },
  already_matches: { label: 'Già coerente', tone: 'positive' },
  skipped: { label: 'Non inserito', tone: 'neutral' },
  unresolved: { label: 'Non risolto', tone: 'negative' },
  accepted: { label: 'Valore EDI accettato', tone: 'positive' },
  keep_existing: { label: 'Valore esistente mantenuto', tone: 'positive' },
  written: { label: 'Scritto', tone: 'positive' },
};

const modeLabel = (mode: SecapTemplateCompilationMode): string =>
  mode === 'upload' ? 'File utente' : 'Template ufficiale';

const valueLabel = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? '—';
  } catch {
    return '—';
  }
};

const stringProperty = (value: SecapJsonObject, key: string): string | null => {
  const candidate = value[key];
  return typeof candidate === 'string' && candidate.trim().length > 0 ? candidate : null;
};

const sourceLabel = (field: ISecapTemplateCompilationField): string => {
  const provenance = field.provenance;
  return (
    stringProperty(provenance, 'source_code') ??
    stringProperty(provenance, 'indicator_code') ??
    stringProperty(provenance, 'composition_code') ??
    stringProperty(provenance, 'resolution') ??
    field.resolution ??
    '—'
  );
};

const fieldView = (field: ISecapTemplateCompilationField): ISecapTemplateCompilationFieldView => {
  const state = FIELD_STATUS[field.state] ?? { label: field.state, tone: 'neutral' as const };
  const candidate = field.final_value ?? field.proposed_value ?? field.existing_value;
  const proposed = valueLabel(field.proposed_value);
  const existing = field.existing_value === null || field.existing_value === undefined
    ? null
    : valueLabel(field.existing_value);
  const finalValue = field.final_value === null || field.final_value === undefined
    ? null
    : valueLabel(field.final_value);

  return {
    uuid: field.uuid,
    fieldKey: field.field_key,
    sheetName: field.sheet_name,
    cellReference: field.cell_reference,
    sourceLabel: sourceLabel(field),
    valueLabel: valueLabel(candidate),
    proposedValueLabel: proposed,
    existingValueLabel: existing,
    finalValueLabel: finalValue,
    state: field.state,
    stateLabel: state.label,
    tone: state.tone,
    reason: field.reason,
    hasConflict: field.state === 'conflict',
    needsDecision: field.state === 'pending_confirmation' || field.state === 'conflict',
    isRequired: field.is_required,
  };
};

const historyItem = (
  compilation: ISecapTemplateCompilation,
): ISecapTemplateCompilationHistoryItem => {
  const status = COMPILATION_STATUS[compilation.status] ?? {
    label: compilation.status,
    tone: 'neutral' as const,
  };
  return {
    uuid: compilation.uuid,
    fileName: compilation.source_filename,
    mode: compilation.mode,
    modeLabel: modeLabel(compilation.mode),
    status: compilation.status,
    statusLabel: status.label,
    tone: status.tone,
    createdAt: compilation.created_at,
    pendingCount: compilation.pending_count,
    conflictCount: compilation.conflict_count,
    unresolvedCount: compilation.unresolved_count,
  };
};

export const buildSecapTemplateCompilationViewModel = (
  compilations: readonly ISecapTemplateCompilation[],
  selected: ISecapTemplateCompilation | null,
): ISecapTemplateCompilationViewModel => {
  const status = selected
    ? (COMPILATION_STATUS[selected.status] ?? { label: selected.status, tone: 'neutral' as const })
    : { label: 'Nessuna compilazione', tone: 'neutral' as const };

  return {
    compilation: selected,
    history: compilations.map(historyItem),
    fields: (selected?.fields ?? []).map(fieldView),
    statusLabel: status.label,
    statusTone: status.tone,
  };
};
